import { createPersistentItem, globalRef, type Unsubscribe } from '@amazon/vinyl-util'
import { boolean, number, object } from '@amazon/vinyl-validation'
import { AuthStatus } from '@amzn/amp-playback'
import { getAuth } from '@/auth/authRef'
import {
    fetchPendingProfileTransfer,
    type ProfileTransferMessage,
} from '@/profile/selectProfileService'
import { getAuthRecovery } from '@/service/gql'
import { ctx } from '@/util/context'

const { errorHandler } = ctx('profileTransferPrompt')

const PENDING_SOURCE_REPEAT_SECONDS = 24 * 60 * 60

interface TransferMessageHistory {
    readonly pendingSourceShownAtSeconds?: number
    readonly declinedShown?: boolean
}

// Sign-out's storage wipe clears every profile's history, as GH's logout reset does.
function historyItem(profileId: string) {
    return createPersistentItem<TransferMessageHistory>({
        key: `profileTransferMessages:${profileId}`,
        defaultValue: {},
        validator: object({
            pendingSourceShownAtSeconds: number().optional(),
            declinedShown: boolean().optional(),
        }).assert,
    })
}

function nowSeconds(): number {
    return Date.now() / 1000
}

function shouldShow(message: ProfileTransferMessage, history: TransferMessageHistory): boolean {
    switch (message) {
        case 'PENDING_TARGET':
            return true
        case 'PENDING_SOURCE': {
            const shownAt = history.pendingSourceShownAtSeconds
            return shownAt === undefined || nowSeconds() - shownAt >= PENDING_SOURCE_REPEAT_SECONDS
        }
        case 'DECLINED_SOURCE':
            return !history.declinedShown
    }
}

function shownHistory(
    message: ProfileTransferMessage,
    history: TransferMessageHistory
): TransferMessageHistory | null {
    switch (message) {
        case 'PENDING_TARGET':
            return null
        case 'PENDING_SOURCE':
            return { ...history, pendingSourceShownAtSeconds: nowSeconds() }
        case 'DECLINED_SOURCE':
            return { ...history, declinedShown: true }
    }
}

const checkRef = globalRef<{ check: Promise<void> | null; session: number }>(() => ({
    check: null,
    session: 0,
}))

async function runCheck(session: number): Promise<void> {
    const transfer = await fetchPendingProfileTransfer()
    if (!transfer) return
    const history = historyItem(transfer.profileId)
    const shown = await history.get()
    if (!shouldShow(transfer.message, shown)) return
    const { openProfileTransferModal } = await import('@/messaging/systemModals')
    // An auth recovery prompt pre-empts this one, and a sign-out mid-check makes it stale.
    if (getAuthRecovery().value || checkRef.value.session !== session) return
    const closed = new Promise<void>((resolve) => openProfileTransferModal(transfer, resolve))
    const next = shownHistory(transfer.message, shown)
    if (next) await history.set(next)
    await closed
}

/**
 * Runs once per signed-in session and resolves once the prompt is resolved:
 * nothing to show, or the modal closed. Never rejects, so a failed check can't
 * hold up onboarding.
 */
export function checkPendingProfileTransfer(): Promise<void> {
    if (!getAuth().isSignedIn()) return Promise.resolve()
    const state = checkRef.value
    state.check ??= runCheck(state.session).catch(errorHandler)
    return state.check
}

/** Checks at each sign-in, unless the app launched into the profile picker, where no profile is chosen yet. */
export function installProfileTransferPrompt(): Unsubscribe {
    const launchedChoosingProfile = location.pathname.startsWith('/identity/')
    return getAuth().status.onData((status) => {
        if (status === AuthStatus.SIGNED_IN) {
            if (!launchedChoosingProfile) checkPendingProfileTransfer().catch(errorHandler)
        } else if (status === AuthStatus.SIGNED_OUT) {
            const state = checkRef.value
            state.check = null
            state.session++
        }
    })
}
